import React, { useContext } from 'react';

import UIContext from 'components/Utils/UIContext';
import { faClone, faClose, faEdit, faEyeSlash, faPlus, faTrash } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActionButton from "components/action/ActionButton";

import { useTranslation } from "react-i18next";

import "./FishboneBranchGeneralTab.sass";
import { Button, Flex, Tag, Tooltip } from 'antd';
import { keyBy, sortBy, sumBy, uniq } from 'lodash';
import Weight from 'components/controls/Weight';
import { colorToPercent } from 'utils/ranking';
import dayjs from 'dayjs';
import { IconPicker } from 'components/IconPicker';
import { MiniSlider } from 'components/controls/MiniSlider';
import { getItemAlteredByScenario } from 'utils/rules';
import { FishboneGeneralLinks } from './FishboneGeneralLinks';

interface FishboneBranchGeneralTabProps {
    selection: any;
    onEdit: Function;
    onClone: Function;
    onClose: Function;
    onDisable: Function;
    onDelete: Function;
    onChange: Function;
    scenario: any;

    risks: any[];
    fishboneBranchs: any[];
    onAddItem?: Function;

    stakes: any[];
    indicators: any[];
    stakeholders: any[];
    select: Function;
    createStakeImpact: Function;
    deleteStakeImpact: Function;
    updateImpactStrength: Function;

}

export const FishboneBranchGeneralTab = (props: FishboneBranchGeneralTabProps) => {

    const { fishboneBranchs = [] } = props;
    const { t } = useTranslation();
    const uiContext = useContext(UIContext);

    const selection = props.selection && props.selection.__typename == "FishboneBranch" ? getItemAlteredByScenario(props.selection, props.scenario) : null
    const selectionItems = selection?.items.map((p) => getItemAlteredByScenario(p, props.scenario)) || []

    let pointsWeight = 0;
    selectionItems.forEach((p) => (pointsWeight += p.weight || 1));

    const completion =
        selectionItems && selectionItems.length > 0
            ? sumBy(selectionItems, (p) => (p.completion || 0) * (p.weight || 1)

            ) /
            pointsWeight
            : 0;

    const vulnerability =
        selectionItems && selectionItems.length > 0
            ? sumBy(
                selectionItems,
                (p) =>
                    colorToPercent(p.trend !== null ? p.trend : '50') *
                    (p.weight || 1)
            ) / pointsWeight
            : 50;


    const allIcons: any = uniq(
        fishboneBranchs.map((i: any) => (i.icon ? i.icon : 'faCrosshairs'))
    ).sort();


    return (
        <div className="FishboneBranchGeneralTab noselection">

            <Flex vertical>

                <Flex className="mb-1" align="center">


                    <div
                        className="IconPicker"
                        style={{ marginRight: '1rem' }}
                    >
                        <IconPicker
                            value={
                                selection?.icon ||
                                'faCrosshairs'
                            }
                            favorites={allIcons}
                            disabled={!!props.scenario || !uiContext.canEdit}
                            onChange={
                                props.scenario || !uiContext.canEdit
                                    ? () => { }
                                    : (v) =>
                                        props.onChange(
                                            props.selection
                                                .id,
                                            { icon: v }
                                        )
                            }
                        />
                    </div>

                    <div className="w-100 text-left">
                        <div className="title">{selection?.name}</div>
                        <small>{t("models.fishbone_branch.name")}</small>
                    </div>

                    <Tooltip title={t('actions.close')} placement='left'>
                        <Button
                            shape="circle"
                            variant="solid"
                            className="mini ml-auto"
                            tooltip={t('models.item.actions.edit')}
                            style={{ marginRight: '-0.9rem' }}
                            onClick={props.onClose}
                        >
                            <FontAwesomeIcon icon={faClose} />
                        </Button></Tooltip>
                </Flex>

                {selection?.description && (
                    <div className="fishbone-description">{selection?.description}</div>
                )}

                <ul>
                    {selection?.responsible && <li className="EditableListItem">
                        <div style={{ width: '45%', textAlign: 'left' }}>
                            {t("models.fishbone_branch.fields.responsible")}
                        </div>
                        <div>
                            {selection?.responsible}
                        </div>
                    </li>}

                    <li className="EditableListItem">
                        <div style={{ width: '45%', textAlign: 'left' }}>
                            {t("models.fishbone_branch.fields.completion")}
                        </div>
                        <div>
                            {completion.toFixed(0) + "%"}
                        </div>
                    </li>

                    <li className="EditableListItem">
                        <div style={{ width: '45%', textAlign: 'left' }}>
                            {t("models.fishbone_branch.fields.trend")}
                        </div>
                        <div>
                            <MiniSlider
                                value={vulnerability}
                                key={props.selection?.id}
                                min={0}
                                max={100}
                                disabled={true}

                                className="highContrast"
                                style={{ width: '150px' }}
                                readOnly={true}
                            ></MiniSlider>
                        </div>
                    </li>

                    <li className="EditableListItem">
                        <div style={{ width: '45%', textAlign: 'left' }}>
                            {t("models.fishbone_branch.fields.weight")}
                        </div>
                        <div>
                            <Weight disabled={!uiContext.canEdit} onClick={() => props.onChange(selection?.id, { weight: selection?.weight > 2 ? 1 : selection?.weight + 1 })}>{selection?.weight}</Weight>
                        </div>
                    </li>

                    <li className="EditableListItem">
                        <div style={{ width: '45%', textAlign: 'left' }}>
                            {t("models.fishbone_branch.fields.dates")}
                        </div>
                        <div>
                            {
                                <small>
                                    {selection?.startOn ? dayjs(
                                        selection?.startOn
                                    ).format('DD/MM/YYYY') : t("messages.not_defined")}
                                </small>
                            }
                            {selection?.endOn &&
                                !selection?.startOn ?
                                'Until ' : selection?.endOn ? <small className="mr-05 ml-05">-</small> : null}
                            {selection?.endOn && (
                                <small>
                                    {dayjs(selection?.endOn).format(
                                        'DD/MM/YYYY'
                                    )}
                                </small>
                            )}
                        </div>
                    </li>
                </ul>

                <div className="Actions mt-2 mb-4 text-center" data-testid="fishbone-branch-actions">

                    {props.onAddItem && <ActionButton
                        className="small ml-05"
                        data-testid="add-item-button"
                        tooltip={t('models.fishbone_branch.actions.add')}
                        onClick={(e) => props.onAddItem(e)}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </ActionButton>}

                    {props.onEdit && <ActionButton
                        className="small"
                        data-testid="action-edit"
                        tooltip={t('models.fishbone_branch.actions.edit')}
                        onClick={() => props.onEdit(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faEdit} />
                    </ActionButton>}

                    {props.onClone && <ActionButton
                        className="small"
                        data-testid="action-clone"
                        tooltip={t('models.fishbone_branch.actions.clone')}
                        onClick={() => props.onClone(props.selection)}
                    >
                        <FontAwesomeIcon icon={faClone} />
                    </ActionButton>}

                    {props.onDisable && <ActionButton
                        className="small"
                        data-testid="action-disable"
                        tooltip={t('actions.disable')}
                        onClick={() => props.onDisable(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faEyeSlash} />
                    </ActionButton>}

                    {props.onDelete && <ActionButton
                        className="small"
                        data-testid="action-delete"
                        tooltip={t('models.fishbone_branch.actions.delete')}
                        onClick={() => props.onDelete(props.selection.id)}
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </ActionButton>}
                </div>

                <FishboneGeneralLinks
                    selection={props.selection}
                    nodeType="branch"
                    scenario={props.scenario}
                    select={props.select}
                    risks={props.risks}
                    stakes={props.stakes}
                    indicators={props.indicators}
                />

                {false && (
                    <div className="d-flex flex-column flex-grow mt-3">
                        <div className="text-left mb-05">
                            {t('models.stake.fields.risks.name')}
                        </div>
                        <div className="text-left">
                            {realRisks && realRisks.length ? (
                                sortBy(realRisks, 'name').map(
                                    (r: any, index) => {
                                        const impactedStake = r.impactedStakes.find(ii => ii.stakeId == selection?.id);
                                        return (
                                            <Tag.CheckableTag
                                                key={index}
                                                checked={impactedStake?.id}
                                                onClick={async (e) => {

                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    if (
                                                        !impactedStake ||
                                                        !impactedStake.id
                                                    ) {
                                                        props.createStakeImpact(
                                                            r.id,
                                                            selection?.id,

                                                        );
                                                    } else if (
                                                        impactedStake.strength <
                                                        3
                                                    ) {
                                                        props.updateImpactStrength(
                                                            impactedStake,
                                                            impactedStake.strength +
                                                            1
                                                        );
                                                    } else {
                                                        props.deleteStakeImpact(
                                                            impactedStake.id,
                                                            r.id
                                                        );
                                                    }
                                                }}
                                                className={[
                                                    'ImpactedStake tag'
                                                ].join(' ')}
                                            >

                                                <span className="tag__label" title={r.name}>{r.name}</span>


                                            </Tag.CheckableTag>
                                        );
                                    }
                                )
                            ) : (
                                <div
                                    className="text-center"
                                    style={{ color: 'var(--ant-color-text-tertiary)' }}
                                >
                                    {t('models.stake.no_x')}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {false && (
                    <div className="d-flex flex-column flex-grow mt-4 mb-4">
                        <div className="text-left mb-05">
                            {t('models.stake.fields.risks.name_positive')}
                        </div>
                        <div className="text-left">
                            {realOpportunities && realOpportunities.length ? (
                                sortBy(realOpportunities, 'name').map(
                                    (r: any, index) => {
                                        const impactedStake = r.impactedStakes.find(ii => ii.stakeId == selection?.id);

                                        return (
                                            <Tag.CheckableTag
                                                key={index}
                                                checked={impactedStake?.id}
                                                onClick={async (e) => {

                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    if (
                                                        !impactedStake ||
                                                        !impactedStake.id
                                                    ) {
                                                        props.createStakeImpact(
                                                            r.id,
                                                            selection?.id,

                                                        );
                                                    } else if (
                                                        impactedStake.strength <
                                                        3
                                                    ) {
                                                        props.updateImpactStrength(
                                                            impactedStake,
                                                            impactedStake.strength +
                                                            1
                                                        );
                                                    } else {
                                                        props.deleteStakeImpact(
                                                            impactedStake.id,
                                                            r.id
                                                        );
                                                    }
                                                }}
                                                className={[
                                                    'ImpactedStake tag'
                                                ].join(' ')}
                                            >

                                                <span className="tag__label" title={r.name}>{r.name}</span>


                                            </Tag.CheckableTag>
                                        );
                                    }
                                )
                            ) : (
                                <div
                                    className="text-center"
                                    style={{ color: 'var(--ant-color-text-tertiary)' }}
                                >
                                    {t('models.stake.no_x')}
                                </div>
                            )}
                        </div>
                    </div>
                )}


            </Flex>
        </div >
    );
}